import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { Handshake, Users, AlertTriangle } from "lucide-react";
import heroImg from "@/assets/mentoring/mentoring-hero.jpg";
import marginsImg from "@/assets/mentoring/mentoring-margins.jpg";
import opsImg from "@/assets/mentoring/mentoring-operations.jpg";
import fashionImg from "@/assets/mentoring/testimonial-fashion.jpg";
import coffeeImg from "@/assets/mentoring/testimonial-coffee.jpg";

const MentoringVipLanding = () => {
  const { lang } = useLanguage();
  const es = lang === "es";

  const scrollToPlans = () => {
    document.getElementById("planes-vip")?.scrollIntoView({ behavior: "smooth" });
  };

  const pains = [
    {
      icon: Handshake,
      iconColor: "text-yellow-600",
      subItalic: true,
      title: es
        ? "Mi gestoría solo me habla de impuestos, pero nadie me dice cómo elegir proveedores o cómo negociar con ellos."
        : "My accountant only talks taxes, but nobody tells me how to choose suppliers or negotiate with them.",
      sub: es
        ? "Deja la teoría. Tu mentor es alguien 'de trinchera', con experiencia real en la calle, que te acompaña a tomar las decisiones que de verdad dan dinero."
        : "Forget theory. Your mentor is a 'boots-on-the-ground' operator with real street experience who guides you to the decisions that actually make money.",
    },
    {
      icon: Users,
      iconColor: "text-blue-500",
      subItalic: true,
      title: es
        ? "El Panel me dice lo que falla, pero necesito a alguien que baje al barro conmigo y me marque paso a paso cómo poner orden y por dónde avanzar."
        : "The Panel tells me what's failing, but I need someone to get in the trenches with me and show me step by step how to put things in order and where to move next.",
      sub: es
        ? "Pasamos a la acción. Te explicamos paso a paso qué tienes que hacer, te ayudamos a revisar contratos y te conectamos con los proveedores que necesitas para que dejes de apagar fuegos."
        : "We move into action. We walk you through step by step what to do, help you review contracts and connect you with the suppliers you need so you can stop firefighting.",
    },
    {
      icon: AlertTriangle,
      iconColor: "text-red-500",
      subItalic: true,
      title: es
        ? "¿La gestión del día a día con tu personal te agota y genera fricciones constantes?"
        : "Does day-to-day management with your staff exhaust you and create constant friction?",
      sub: es
        ? "No esperes a que sea tarde. Tu mentor te ayuda a estructurar roles, incentivos y comunicación para alinear a tu equipo."
        : "Don't wait until it's too late. Your mentor helps you structure roles, incentives and communication to align your team.",
    },
  ];

  const testimonials = [
    {
      img: fashionImg,
      quote: es
        ? "No sabía por dónde empezar a poner orden. Tener a un mentor a mi lado me dio la claridad y la confianza para organizarme paso a paso y tomar por fin el control de todo lo que necesitaba."
        : "I didn't know where to start putting order in place. Having a mentor by my side gave me the clarity and confidence to organize myself step by step and finally take control of everything I needed.",
      name: "Marta R.",
      role: es ? "Dueña, boutique de moda" : "Owner, fashion boutique",
    },
    {
      img: coffeeImg,
      quote: es
        ? "Estaba perdido y sin saber a quién preguntar. Suerte que utilicé a los profesionales que me recomendó mi mentor para delegar y solucionar el problema."
        : "I was lost and didn't know who to ask. Luckily I used the professionals my mentor recommended to delegate and solve the problem.",
      name: "Diego M.",
      role: es ? "Dueño, cafetería de especialidad" : "Owner, specialty coffee shop",
    },
  ];

  return (
    <div className="bg-background">
      {/* BLOQUE 1 — Hero emocional */}
      <section className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-20 sm:py-28 grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-6">
            <span className="inline-block text-xs tracking-[0.25em] uppercase text-muted-foreground font-body">
              {es ? "Tu Mentor" : "Your Mentor"}
            </span>
            <h1 className="font-body text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight text-balance mb-6">
              {es
                ? "Abajo las dudas. No estás solo en el Retail."
                : "Doubts down. You're not alone in Retail."}
            </h1>
            <h2 className="font-body text-lg sm:text-xl text-muted-foreground leading-relaxed font-normal">
              {es
                ? "El Club Pro te da los datos. Tu Mentor te dice qué hacer con ellos para dejar de apagar fuegos y empezar a dirigir tu imperio."
                : "The Club Pro gives you the data. Your Mentor tells you what to do with it — stop firefighting and start running your empire."}
            </h2>
            <div>
              <Button variant="hero" size="xl" onClick={scrollToPlans} className="shadow-xl">
                {es ? "Ver Planes" : "See Plans"}
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-primary/20 rounded-3xl blur-2xl" aria-hidden="true" />
            <img
              src={heroImg}
              alt={es ? "Sesión de mentoría retail con Club Pro" : "Retail mentoring session with Club Pro"}
              width={1280}
              height={960}
              className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
              loading="lazy"
             decoding="async" />
          </div>
        </div>
      </section>

      {/* BLOQUE 2 — Agitación del dolor */}
      <section className="border-b border-border bg-secondary/30">
        <div className="max-w-6xl mx-auto px-4 py-20 sm:py-24">
          <h2 className="font-body text-3xl sm:text-4xl font-extrabold text-center mb-14">
            {es ? "¿Te identificas con alguno de estos bloqueos?" : "Do you recognize any of these blockers?"}
          </h2>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {pains.map((p, i) => (
              <div
                key={i}
                className="bg-card rounded-2xl border border-border shadow-lg hover:shadow-xl transition-shadow p-8 flex flex-col"
              >
                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mb-6">
                  <p.icon className={`w-7 h-7 ${(p as any).iconColor ?? "text-foreground"}`} strokeWidth={1.5} />
                </div>
                <p className="font-body text-xl font-bold leading-snug mb-6">{p.title}</p>
                <div className="mt-auto pt-4 border-t border-border">
                  <p className={`font-body text-sm text-muted-foreground leading-relaxed ${(p as any).subItalic ? "italic" : ""}`}>{p.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOQUE 3 — Qué es la Mentoría VIP (zigzag) */}
      <section className="border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-20 sm:py-28 space-y-20 sm:space-y-28">
          <h2 className="font-body text-3xl sm:text-4xl font-extrabold text-center text-balance leading-tight mb-8 sm:mb-4 pb-2">
            {es ? "Así es tener a Retail Lemon a tu lado" : "This is what having Retail Lemon by your side feels like"}
          </h2>

          {/* Fila 1 */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <img
              src={marginsImg}
              alt={es ? "Mentor retail acompañando a dueño de negocio" : "Retail mentor supporting business owner"}
              width={1024}
              height={768}
              className="rounded-2xl shadow-xl w-full h-auto object-cover"
              loading="lazy"
             decoding="async" />
            <div className="space-y-4">
              <h3 className="font-body text-3xl sm:text-4xl font-extrabold">
                {es ? "No estás solo. ¡Rodéate de buenos profesionales!" : "You're not alone. Surround yourself with great professionals!"}
              </h3>
              <p className="font-body text-lg text-muted-foreground leading-relaxed">
                {es
                  ? "Se acabó tomar decisiones críticas en solitario. Tienes a tu disposición a expertos de trinchera que entienden tu día a día y te respaldan en cada paso."
                  : "No more making critical decisions alone. You have boots-on-the-ground experts at your disposal who understand your day-to-day and back you up every step of the way."}
              </p>
            </div>
          </div>

          {/* Fila 2 — invertida */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-4 md:order-2">
              <h3 className="font-body text-3xl sm:text-4xl font-extrabold">
                {es ? "Una metodología de éxito contrastado" : "A proven success methodology"}
              </h3>
              <p className="font-body text-lg text-muted-foreground leading-relaxed">
                {es
                  ? "Años de mentoría en retail nos avalan. Deja de improvisar y aplica los mismos sistemas y estrategias que ya han utilizado con éxito decenas de dueños de negocio."
                  : "Years of retail mentoring back us up. Stop improvising and apply the same systems and strategies that dozens of business owners have already used successfully."}
              </p>
            </div>
            <img
              src={opsImg}
              alt={es ? "Auditoría operativa de negocio con mentor" : "On-site operations audit with mentor"}
              width={1024}
              height={768}
              className="rounded-2xl shadow-xl w-full h-auto object-cover md:order-1"
              loading="lazy"
             decoding="async" />
          </div>
        </div>
      </section>

      {/* BLOQUE 4 — Testimonios */}
      <section className="border-b border-border bg-secondary/30">
        <div className="max-w-6xl mx-auto px-4 py-20 sm:py-24">
          <h2 className="font-body text-3xl sm:text-4xl font-extrabold text-center mb-14">
            {es
              ? "Otros dueños de retail que eligieron no estar solos"
              : "Other retail owners who chose not to go it alone"}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <figure
                key={i}
                className="bg-card rounded-2xl border border-border shadow-lg p-8 sm:p-10 flex flex-col gap-6"
              >
                <blockquote className="font-body text-xl sm:text-2xl italic leading-snug">
                  “{t.quote}”
                </blockquote>
                <figcaption className="flex items-center gap-4 mt-auto">
                  <img
                    src={t.img}
                    alt={t.name}
                    width={80}
                    height={80}
                    className="w-16 h-16 rounded-full object-cover border-2 border-primary"
                    loading="lazy"
                   decoding="async" />
                  <div>
                    <p className="font-body font-bold text-foreground">{t.name}</p>
                    <p className="font-body text-sm text-muted-foreground">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MentoringVipLanding;
